export const WIRE_META = {
  links: 21,
  stories: 13,
  updated: "Sep 2026",
};

export const SIGNALS = [
  {
    slug: "the-case-for-compute",
    kicker: "DCYB Study",
    date: "September 2026",
    sortDate: "2026-09-08",
    meta: "95 sources · Health, water, energy, economics",
    headline: "The Case for Compute",
    dek: "Why the next generation of AI infrastructure serves humanity, and why the public debate runs on the wrong numbers.",
    body: [
      "Three in four Americans now say they would oppose a data center near their home. At least 75 projects worth about $130 billion were blocked or delayed in the first quarter of 2026 alone, and moratorium bills appeared in 14 statehouses plus Congress.",
      "The data centers now rising across the United States are the physical substrate of the most consequential scientific instrument humanity has ever built. The public debate about that infrastructure has detached from the evidence.",
      "This study assembles the primary evidence across four domains — health, water, energy, and economics — and states plainly where the case is strong and where legitimate concerns remain. American golf courses use 30 times more water than every American data center combined. Data centers used 1.5 percent of global electricity in 2024, while compute grew 550 percent from 2010 to 2018 against a 6 percent rise in energy.",
      "It also sets the standard communities should demand: disclose water and power before approval, cool without evaporating stressed local water, pay the full cost of grid connection, bring new firm clean generation with the load, replenish more than is consumed, and train and hire locally.",
    ],
    take:
      "The bottleneck on responsible infrastructure is not capital, and it is not technology. It is people. The AI buildout is the largest opportunity of the decade for anyone willing to learn the trade.",
    stats: [
      ["75%", "Oppose a nearby DC", "Heatmap, Aug 2026"],
      ["$130B", "Blocked or delayed", "75 projects, Q1 2026"],
      ["30x", "Golf vs data centers", "US water use"],
      ["0.015%", "Of US withdrawals", "Data center direct water"],
      ["1.5%", "Global electricity", "2024 · ~3% by 2030"],
      ["2.3M", "Workforce needed", "Uptime FTE forecast"],
    ],
    chart: {
      title: "The water question, to scale",
      subtitle: "Annual US use, billions of gallons. Data center direct use versus two everyday comparisons.",
      points: [
        { label: "Data centers", value: 17.4, display: "17.4B", note: "Direct on-site use", hot: true },
        { label: "Golf courses", value: 532, display: "532B", note: "US course irrigation" },
        { label: "CO River alfalfa", value: 1630, display: "1,630B", note: "Alfalfa and forage" },
      ],
    },
    study: {
      title: "The Case for Compute",
      subtitle:
        "Why the Next Generation of AI Infrastructure Serves Humanity, and Why the Public Debate Runs on the Wrong Numbers",
      author: "Luke Adams, Founder and CEO, Data Center Youngbloods",
      pdf: "/wire/DCYB-Study-The-Case-for-Compute.pdf",
      pages: 21,
    },
    image: {
      src: "/wire/the-case-for-compute-cover.webp",
      alt: "Protesters holding Stop Data Centers and Don't Ruin Texas signs, with the Great Seal of the United States and the DCYB mark.",
      label: "Study cover · open full size",
    },
    sources: "Heatmap · Gallup · IEA · LBNL · USGS · Brookings · Loudoun County · Uptime Institute",
  },
  {
    slug: "the-12-9-billion-signal",
    kicker: "Industry Signal",
    date: "Sep 3, 2026",
    sortDate: "2026-09-03",
    meta: "Study Aug 28, 2026 · Record Sep 3, 2026",
    headline: "Nvidia Confirms the Signal DCYB Charted Six Days Early",
    dek: "The study ran the numbers on August 28. Jensen Huang confirmed them on September 3.",
    body: [
      "Six days before Nvidia confirmed this transaction, we put the $12.9 billion signal on the record.",
      "On August 28, while the deal remained unconfirmed, we published an industry study analyzing Nvidia's reported agreement to acquire Hugging Face and what it would mean for platform neutrality, market concentration, and the infrastructure workforce.",
      "On September 3, Jensen Huang confirmed the agreement at exactly $12,930,300,000.",
      "This is what the DCYB Wire is built to do: identify consequential industry shifts early, quantify their significance, and connect the headline to the physical infrastructure and talent required to support it.",
    ],
    take:
      "Nvidia now holds the compute beneath open AI and the distribution platform above it. Every model still runs inside a physical data center. We are developing the workforce that keeps those facilities operating.",
    stats: [
      ["$12.93B", "Confirmed price", "NVIDIA Blog"],
      ["1.8x", "Mellanox", "Nvidia's largest deal"],
      ["16M", "Builders", "18M+ at announcement"],
      ["3M+", "Models", "On the Hub"],
      ["1M", "Applications", "Spaces"],
      ["500K", "Datasets", "Open commons"],
    ],
    chart: {
      title: "Hugging Face valuation climbs",
      subtitle: "USD billions. The study charted all three points before the confirmation.",
      points: [
        { label: "Aug 2023", value: 4.5, display: "$4.5B", note: "Series D valuation" },
        { label: "Late 2025", value: 7, display: "$7B", note: "Rejected Nvidia offer basis" },
        { label: "Sep 3, 2026", value: 12.93, display: "$12.93B", note: "Confirmed acquisition price", hot: true },
      ],
    },
    study: {
      title: "The $12.9 Billion Signal",
      subtitle:
        "What the Reported Nvidia Acquisition of Hugging Face Means for Open-Source AI, Market Concentration, and the Infrastructure Workforce",
      author: "Luke Adams, CEO, Data Center Youngbloods",
      pdf: "/wire/DCYB-Industry-Study-The-12.9-Billion-Signal.pdf",
    },
    image: {
      src: "/wire/the-12-9-billion-signal-one-pager.webp",
      alt: "DCYB Wire one-pager — Nvidia Confirms the Signal DCYB Charted Six Days Early",
      label: "DCYB Wire one-pager · open full size",
      downloadLabel: "One-pager",
    },
    source: {
      label: "Nvidia announcement",
      url: "https://blogs.nvidia.com/blog/nvidia-to-acquire-hugging-face/",
    },
    sources: "DCYB Industry Study · The Information · NVIDIA Blog",
  },
];

export const FEATURES = [
  {
    date: "Aug 27, 2026",
    outlet: "Economic Insider",
    domain: "economicinsider.com",
    headline:
      "Data Center Youngbloods Expands Workforce Platform as AI Infrastructure Growth Intensifies the Battle for Skilled Talent",
    angle: "Workforce capacity as the next economic constraint on the AI buildout",
    url: "https://economicinsider.com/data-center-youngbloods-expands-workforce-platform-as-ai-infrastructure-growth-intensifies-the-battle-for-skilled-talent/",
  },
  {
    date: "Aug 27, 2026",
    outlet: "Market Daily",
    domain: "marketdaily.com",
    headline:
      "Data Center Youngbloods Expands as AI Infrastructure Growth Puts New Pressure on the Talent Market",
    angle: "Talent as the factor that turns capital into operational capacity",
    url: "https://marketdaily.com/data-center-youngbloods-expands-as-ai-infrastructure-growth-puts-new-pressure-on-the-talent-market/",
  },
  {
    date: "Aug 27, 2026",
    outlet: "US Insider",
    domain: "usinsider.com",
    headline:
      "Luke Adams Is Building Data Center Youngbloods to Create a New Path Into America's Digital Infrastructure Workforce",
    angle: "Making the industry easier to enter for talent and easier to hire for employers",
    url: "https://usinsider.com/luke-adams-is-building-data-center-youngbloods-to-create-a-new-path-into-america-s-digital-infrastructure-workforce/",
  },
  {
    date: "Aug 26, 2026",
    outlet: "US Business News",
    domain: "usbusinessnews.com",
    headline:
      "Luke Adams Expands Data Center Youngbloods as Demand Grows for America's Digital Infrastructure Workforce",
    angle: "DCYB as workforce infrastructure for America's digital economy",
    url: "https://usbusinessnews.com/luke-adams-expands-data-center-youngbloods-as-demand-grows-for-america-s-digital-infrastructure-workforce/",
  },
  {
    date: "Aug 26, 2026",
    outlet: "Business Insider Markets",
    domain: "businessinsider.com",
    headline:
      "Luke Adams Expands Data Center Youngbloods Into a Workforce Platform for the AI Infrastructure Era",
    angle: "Four-layer platform model, seed round exploration, 12 to 36 month roadmap",
    url: "https://markets.businessinsider.com/news/currencies/luke-adams-expands-data-center-youngbloods-into-a-workforce-platform-for-the-ai-infrastructure-era-1036495338",
  },
  {
    date: "Aug 26, 2026",
    outlet: "USA Times",
    domain: "usa-times.news",
    headline:
      "AI Is Driving a Data Center Boom. Luke Adams Is Betting the Next Bottleneck Will Be People",
    angle: "People as the underpriced resource in the infrastructure race",
    url: "https://usa-times.news/ai-is-driving-a-data-center-boom-luke-adams-is-betting-the-next-bottleneck-will-be-people/",
  },
  {
    date: "Aug 3, 2026",
    outlet: "Miami Wire",
    domain: "miamiwire.com",
    headline: "Luke Adams Says the Great Entrepreneurs Don't Panic. They Prepare.",
    angle: "Founder profile — composure and MMA discipline applied to company building",
    url: "https://miamiwire.com/luke-adams-says-the-great-entrepreneurs-don-t-panic-they-prepare/",
  },
  {
    date: "Aug 3, 2026",
    outlet: "CEO Weekly",
    domain: "ceoweekly.com",
    headline:
      "Luke Adams Says Gen Z Isn't Losing Jobs to AI. They're Building the Infrastructure Behind It.",
    angle: "Counter-narrative on AI and jobs — the new careers behind the AI economy",
    url: "https://ceoweekly.com/luke-adams-says-gen-z-isn-t-losing-jobs-to-ai-they-re-building-the-infrastructure-behind-it/",
  },
  {
    date: "Jul 28, 2026",
    outlet: "Big News Network",
    domain: "bignewsnetwork.com",
    headline: "The Trillion-Dollar AI Buildout Has a Workforce Problem Nobody Can Ignore",
    angle: "The $1T+ buildout against a talent pipeline that has not kept pace",
    url: "https://www.bignewsnetwork.com/news/279213038/the-trillion-dollar-ai-buildout-has-a-workforce-problem-nobody-can-ignore",
  },
  {
    date: "Jul 28, 2026",
    outlet: "IPS News",
    domain: "ipsnews.net",
    headline:
      "Sustainable AI Depends on More Than Clean Energy. It Depends on People",
    angle: "Sustainability framing — human infrastructure alongside clean energy",
    url: "https://ipsnews.net/business/2026/07/28/sustainable-ai-depends-on-more-than-clean-energy-it-depends-on-people/",
  },
  {
    date: "Jul 28, 2026",
    outlet: "Indie Hackers",
    domain: "indiehackers.com",
    headline:
      "Luke Adams Is Betting Costa Rica Will Become Latin America's Next AI Infrastructure Hub",
    angle: "Pura Vida Data Centers — 100 to 120 MW hyperscale campus in Guanacaste",
    url: "https://www.indiehackers.com/post/luke-adams-is-betting-costa-rica-will-become-latin-america-s-next-ai-infrastructure-hub-jlLsZOemz1Z9IWz9kh25",
  },
  {
    date: "Jul 28, 2026",
    outlet: "Contributor Posts",
    domain: "amazonaws.com",
    headline:
      "He Built the Audience Before the Company. Why Luke Adams Believes That's the Future of Entrepreneurship.",
    angle: "Audience-first company building — LinkedIn as the growth engine",
    url: "https://s3.amazonaws.com/contributor-posts/he-built-the-audience-before-the-company-why-luke-adams-believes-thats-the-future-of-entrepreneurship.html",
  },
  {
    date: "Jul 24, 2026",
    outlet: "Daily Guardian Europe",
    domain: "dailyguardian.eu",
    headline: "Why Greece's Islands Matter to the Future of Digital Infrastructure",
    angle: "Energy, grid modernization, and workforce readiness through the Greek islands lens",
    url: "https://dailyguardian.eu/why-greeces-islands-matter-to-the-future-of-digital-infrastructure/",
  },
];

export const LATEST_ARTICLE = [
  ...SIGNALS.map((s) => ({
    date: s.date,
    sortDate: s.sortDate,
    outlet: `DCYB Wire · ${s.kicker}`,
    headline: s.headline,
    url: `/wire#signal-${s.slug}`,
    external: false,
  })),
  ...FEATURES.map((a) => ({ ...a, external: true })),
].sort((a, b) => new Date(b.sortDate || b.date) - new Date(a.sortDate || a.date))[0];

export const SYNDICATION = {
  date: "Jul 11, 2026",
  headline:
    "Data Center Youngbloods Expands Global Talent Pipeline as Demand for Digital Infrastructure Accelerates",
  outlets: [
    {
      name: "Globe Newswire",
      domain: "globenewswire.com",
      url: "https://www.globenewswire.com/news-release/2026/07/11/3325790/0/en/Data-Center-Youngbloods-Expands-Global-Talent-Pipeline-as-Demand-for-Digital-Infrastructure-Accelerates.html",
    },
    {
      name: "AP News",
      domain: "apnews.com",
      url: "https://apnews.com/press-release/globenewswire-mobile/press-release-dbe0092891b4670630efd088239e1908",
    },
    {
      name: "Yahoo Finance",
      domain: "yahoo.com",
      url: "https://finance.yahoo.com/technology/articles/data-center-youngbloods-expands-global-043200559.html",
    },
    {
      name: "Business Insider",
      domain: "businessinsider.com",
      url: "https://markets.businessinsider.com/news/stocks/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates-1036316189",
    },
    {
      name: "Benzinga",
      domain: "benzinga.com",
      url: "https://www.benzinga.com/pressreleases/26/07/g60402908/data-center-youngbloods-expands-global-talent-pipeline-demand-digital-infrastructure-accelerates",
    },
    {
      name: "StreetInsider",
      domain: "streetinsider.com",
      url: "https://www.streetinsider.com/Globe+Newswire/Data+Center+Youngbloods+Expands+Global+Talent+Pipeline+as+Demand+for+Digital+Infrastructure+Accelerates/26758333.html",
    },
  ],
};

export const REACH_REPORT = {
  date: "Jul 11, 2026",
  placements: "925",
  monthlyReach: "119.8M",
  monthlyVisits: "389M",
  countries: "106",
  regions: [
    { name: "Americas", count: 511 },
    { name: "Worldwide", count: 121 },
    { name: "EMEA", count: 72 },
    { name: "Asia-Pacific", count: 43 },
  ],
  topOutlets: [
    {
      name: "Yahoo! Finance",
      domain: "finance.yahoo.com",
      reach: "35.9M",
      country: "USA",
      url: "https://finance.yahoo.com/technology/articles/data-center-youngbloods-expands-global-043200559.html",
    },
    {
      name: "Associated Press",
      domain: "apnews.com",
      reach: "28.4M",
      country: "USA",
      url: "https://apnews.com/press-release/globenewswire-mobile/press-release-dbe0092891b4670630efd088239e1908",
    },
    {
      name: "KTLA 5 Los Angeles",
      domain: "ktla.com",
      reach: "4.9M",
      country: "USA",
      url: "https://www.ktla.com/business/press-releases/globenewswire/9760691/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates",
    },
    {
      name: "Markets Insider",
      domain: "businessinsider.com",
      reach: "2.8M",
      country: "USA",
      url: "https://markets.businessinsider.com/news/stocks/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates-1036316189",
    },
    {
      name: "Benzinga",
      domain: "benzinga.com",
      reach: "2.1M",
      country: "USA",
      url: "https://www.benzinga.com/pressreleases/26/07/g60402908/data-center-youngbloods-expands-global-talent-pipeline-demand-digital-infrastructure-accelerates",
    },
    {
      name: "Fox 8 Cleveland",
      domain: "fox8.com",
      reach: "1.9M",
      country: "USA",
      url: "https://www.fox8.com/business/press-releases/globenewswire/9760691/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates",
    },
    {
      name: "WGN 9 Chicago",
      domain: "wgntv.com",
      reach: "1.7M",
      country: "USA",
      url: "https://www.wgntv.com/business/press-releases/globenewswire/9760691/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates",
    },
    {
      name: "Yahoo! Finance UK",
      domain: "uk.finance.yahoo.com",
      reach: "1.5M",
      country: "UK",
      url: "https://uk.finance.yahoo.com/news/data-center-youngbloods-expands-global-043200559.html",
    },
  ],
};

export const QUOTE_BANK = [
  {
    quote:
      "The biggest problem in our industry right now is that infrastructure is scaling faster than the workforce pipeline needed to build and operate it.",
    source: "Business Insider Markets",
  },
  {
    quote:
      "Everyone is asking where the power will come from. Far fewer people are asking where the people will come from.",
    source: "IPS News",
  },
  {
    quote:
      "The world sees ChatGPT. We see thousands of people working behind the scenes to make experiences like that possible.",
    source: "CEO Weekly",
  },
  {
    quote:
      "The narrative has become that AI is taking jobs. What people aren't talking about is how many entirely new careers AI is creating.",
    source: "CEO Weekly",
  },
  {
    quote:
      "The best leaders I've met aren't the loudest people in the room. They're the people who stay calm when everyone else is panicking.",
    source: "Miami Wire",
  },
  {
    quote:
      "The biggest mistake founders make isn't building the wrong product. It's building in silence.",
    source: "Contributor Posts",
  },
  {
    quote:
      "Nvidia now holds the compute beneath open AI and the distribution platform above it. Every model still runs inside a physical data center.",
    source: "DCYB Wire · Industry Signal",
  },
  {
    quote:
      "American golf courses use 30 times more water than every American data center combined.",
    source: "DCYB Study · The Case for Compute",
  },
];

export const WIRE_QUOTES = QUOTE_BANK.slice(0, 3);
